import { useState } from 'react';
import { DataCard } from '@/reports/index';
import { peopleTypes } from '@/reports/types';
import { Accordion, styled, Text, Box, Card } from '@/theme/index';
import { DateUtil, commaFormatCurrency } from '@/utils/index';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  '.MuiAccordionDetails-root': {
    padding: 0,
  },
  '.MuiAccordionSummary-root': {
    flexDirection: 'row-reverse',
    padding: theme.spacing(0),
  },
  '.MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
    marginLeft: theme.spacing(1.25),
  },
  ':hover': {
    boxShadow: 'none',
  },
  border: 'none',
}));

interface CriminalDataCardProps {
  criminalData: peopleTypes.Criminals[number];
  thumbRating?: () => JSX.Element;
}

interface CriminalCardSegmentProps {
  criminalData: peopleTypes.Criminals[number];
  offenseData: peopleTypes.Offenses[number];
}

const StyledDataRow = styled(Box)(() => ({
  marginBottom: '5px',
  display: 'flex',
  gap: '5px',
}));

const CriminalCardSegment = ({
  offenseData,
  criminalData,
}: CriminalCardSegmentProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const dateParser = new DateUtil();

  const offenseTitle = () => {
    if (offenseData.court?.offense) {
      return offenseData.court.offense;
    }

    if (offenseData.arrest?.offense) {
      return offenseData.arrest.offense;
    }
    return offenseData.description;
  };

  const dataRow = (label: string, datum: string) => {
    return (
      <StyledDataRow>
        <Text variant="body1" sx={{ fontWeight: 'bold' }}>
          {label}
        </Text>
        <Text variant="body1">{datum}</Text>
      </StyledDataRow>
    );
  };

  const location = () => {
    let locationString = '';

    if (criminalData.county) {
      locationString += `${criminalData.county.toLocaleUpperCase()} COUNTY, `;
    }

    if (criminalData.state) {
      locationString += criminalData.state;
    } else {
      locationString += 'Unknown State';
    }

    return dataRow('Location', locationString);
  };

  const courtDate = () => {
    if (offenseData.court?.date?.full) {
      const date = dateParser.parseDateFromString(
        offenseData.court.date.full,
        'D'
      );
      if (date) return dataRow('Court Date', date);
    }
  };

  const offenseDate = () => {
    if (offenseData.date?.full) {
      const date = dateParser.parseDateFromString(offenseData.date.full, 'D');
      if (date) return dataRow('Date of Offense', date);
    }
  };

  const caseNumber = () => {
    if (offenseData.court?.case_number) {
      return dataRow('Court Case Number', offenseData.court.case_number);
    }
  };

  const courtDescription = () => {
    if (offenseData.court?.description) {
      return dataRow('Court Case Description', offenseData.court.description);
    }
  };

  const courtDisposition = () => {
    if (offenseData.court?.disposition) {
      return dataRow('Court Disposition', offenseData.court.disposition);
    }
  };

  const courtOffense = () => {
    if (offenseData.court?.offense) {
      return dataRow('Court Offense', offenseData.court.offense);
    }
  };

  const courtStatute = () => {
    if (offenseData.court?.statute) {
      return dataRow('Court Statute', offenseData.court.statute);
    }
  };

  const courtCosts = () => {
    if (offenseData.court?.costs) {
      return dataRow(
        'Court Costs',
        commaFormatCurrency(parseInt(offenseData.court.costs))
      );
    }
  };

  const courtPlea = () => {
    if (offenseData.court?.plea) {
      return dataRow('Court Plea', offenseData.court.plea);
    }
  };

  const courtFines = () => {
    return dataRow(
      'Court Fines',
      offenseData?.court?.fine
        ? commaFormatCurrency(parseInt(offenseData.court.fine))
        : '-'
    );
  };

  const nameOnRecord = () => {
    if (criminalData.offender?.names[0]?.full) {
      return dataRow('Name on Record', criminalData.offender.names[0].full);
    }
  };

  const offenderAddress = () => {
    if (criminalData.address?.full) {
      return dataRow("Offender's Address", criminalData.address.full);
    }
  };

  const dateOfBirth = () => {
    if (criminalData.offender?.dobs[0]?.date?.full) {
      const date = dateParser.parseDateFromString(
        criminalData.offender.dobs[0].date.full,
        'D'
      );
      if (date) return dataRow('Date of Birth', date);
    }
  };

  const criminalRace = () => {
    if (criminalData.offender?.race) {
      return dataRow('Race', criminalData.offender.race);
    }
  };

  return (
    <Card variant="outlined">
      <Box>
        <Text variant="h2">{offenseTitle()}</Text>
      </Box>
      {location()}
      {courtDate()}
      {offenseDate()}
      {caseNumber()}
      {courtDescription()}
      {courtDisposition()}
      {courtOffense()}
      {courtStatute()}
      {courtCosts()}
      {courtPlea()}
      {courtFines()}
      <StyledAccordion
        display={`Show ${expanded ? 'less' : 'more'} about this record`}
        defaultExpanded
        square
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <Card variant="outlined">
          <Text variant="h2">Identifying Info on Record</Text>
          {nameOnRecord()}
          {offenderAddress()}
          {dateOfBirth()}
          {criminalRace()}
        </Card>
      </StyledAccordion>
    </Card>
  );
};

export function CriminalDataCard({
  criminalData,
  thumbRating,
}: CriminalDataCardProps) {
  const customCardBody = () => {
    return (
      <Box>
        {criminalData?.offenses.map((offense, index) => {
          return (
            <CriminalCardSegment
              offenseData={offense}
              criminalData={criminalData}
              key={index}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <DataCard
      thumbRating={thumbRating}
      cardStyles={{ mb: 1.5 }}
      customBody={customCardBody()}
    />
  );
}
